import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import { EVocabularyBookStudyType } from '@datatype/iyagigo/type';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as vocabularyBookActions } from '@store/slice/vocabulary-book-slice';
import {
  useModifyVocabularyWorkbookMutation,
  useVocabularyWorkbookBookmarkPageQuery,
  useVocabularyWorkbookIncorrectNotePageQuery,
} from '@hook/iyagigo/useApi';
import { useNavigate } from 'react-router-dom';
import { createVocabularyBookPath } from '@constant/iyagigo/route-path';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useInfiniteScroll } from '@hook/common/useScroll';
import NoteTemplate, { INoteItem } from '@component/iyagigo/common/note-template';

const DescriptionContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  padding: ${size.MARGIN_1};
  background-color: ${color.BROWN_4};
  width: 100%;
`;

const KeywordContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;

const MeaningContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const VocabularyBookNote: React.FC = () => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const vocabularyBookStudyType =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.vocabularyBookStudyType) ??
    EVocabularyBookStudyType.BOOKMARK_NOTE;
  const isSubscriber = useMySubscriptionQuery().data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  // ############### hook ###############
  const navigate = useNavigate();

  // ############### api ###############
  const languageCode = studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

  const mutation = useModifyVocabularyWorkbookMutation(languageCode, null, null, null);
  const bookmarkWorkbookQuery = useVocabularyWorkbookBookmarkPageQuery(languageCode);
  const bookmarkRepeatQuery = useVocabularyWorkbookBookmarkPageQuery(languageCode, true);
  const incorrectNoteWorkbookQuery = useVocabularyWorkbookIncorrectNotePageQuery(languageCode);
  const incorrectNoteRepeatQuery = useVocabularyWorkbookIncorrectNotePageQuery(languageCode, true);
  const workbookQuery =
    vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE
      ? incorrectNoteWorkbookQuery
      : bookmarkWorkbookQuery;
  const workbookData = useMemo(
    () => workbookQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [workbookQuery.data],
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: workbookQuery.hasNextPage ?? false,
    isFetchingNextPage: workbookQuery.isFetchingNextPage ?? false,
    fetchNextPage: workbookQuery.fetchNextPage,
    root: scrollContainerRef,
  });

  // ############### handler ###############
  const handleRepeatToggle = async (workbookKey: string, currentRepeat: boolean) => {
    let request;
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        request = { workbookKey, isBookmarkRepeat: !currentRepeat };
        break;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        request = { workbookKey, isIncorrectRepeat: !currentRepeat };
        break;
      default:
        return;
    }
    await mutation.mutateAsync(request);
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        bookmarkRepeatQuery.refetch();
        break;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        incorrectNoteRepeatQuery.refetch();
        break;
    }
  };

  const handleRemoveClick = (workbookKey: string) => {
    const isIncorrect = vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE;
    dispatch(
      modalActions.showDialogModal({
        type: 'remove-confirm',
        data: { workbookKey, domain: 'vocabulary-book', isIncorrect },
      }),
    );
  };

  const repeatCount = workbookData.filter((item) => {
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        return item.isBookmarkRepeat;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        return item.isIncorrectRepeat;
      default:
        return false;
    }
  }).length;

  const handleRepeatStudyClick = () => {
    // 무료회원 구독 Wall (access) — 반복학습은 Premium 전용
    if (!isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        dispatch(vocabularyBookActions.configureVocabularyBookBookmarkNote({}));
        break;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        dispatch(vocabularyBookActions.configureVocabularyBookIncorrectNote({}));
        break;
    }
    navigate(createVocabularyBookPath('study'));
  };

  const handleItemClick = (workbookKey: string) => {
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        dispatch(vocabularyBookActions.configureVocabularyBookBookmarkNote({ workbookKey }));
        break;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        dispatch(vocabularyBookActions.configureVocabularyBookIncorrectNote({ workbookKey }));
        break;
    }
    navigate(createVocabularyBookPath('study'));
  };

  const items: INoteItem[] = workbookData.map((vocabularyNoteWorkbook) => {
    const aiVocabulary = vocabularyNoteWorkbook.aiVocabulary;
    const meaning = aiVocabulary.meaningKo?.join(', ') ?? '';
    let isRepeat;
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        isRepeat = !!vocabularyNoteWorkbook.isBookmarkRepeat;
        break;
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        isRepeat = !!vocabularyNoteWorkbook.isIncorrectRepeat;
        break;
      default:
        isRepeat = false;
        break;
    }

    return {
      key: vocabularyNoteWorkbook.key,
      isRepeat,
      itemContent: (
        <KeywordContainer>
          <TextBlock
            text={aiVocabulary.source}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
          />
          <MeaningContainer>
            <TextBlock
              text={meaning}
              design={{
                text: { size: 'small', color: color.GRAY_1, bold: true, oneline: true },
                box: { width: '100%', alignX: 'left' },
              }}
            />
          </MeaningContainer>
        </KeywordContainer>
      ),
    };
  });

  const description = (
    <DescriptionContainer>
      <TextBlock
        text={
          vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE
            ? '모르는 단어를 <strong>반복학습 </strong>'
            : '저장한 단어로 <strong>반복학습 </strong>'
        }
        design={{ text: { size: 'medium', color: color.BLACK_2 } }}
      />
      <IconBlock icon='repeat' design={{ icon: { size: 'medium', color: color.BROWN_1, kind: 'outlined' } }} />
      <TextBlock text=' 해보세요' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
    </DescriptionContainer>
  );

  return (
    <NoteTemplate
      description={description}
      isPending={workbookQuery.isPending}
      items={items}
      emptyText={
        vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE
          ? '모르는 단어가 없어요'
          : '저장한 단어가 없어요'
      }
      repeatStudyDisabled={repeatCount === 0}
      sentinelRef={sentinelRef}
      scrollContainerRef={scrollContainerRef}
      handleRepeatToggle={handleRepeatToggle}
      handleItemClick={handleItemClick}
      handleRemoveClick={handleRemoveClick}
      handleRepeatStudyClick={handleRepeatStudyClick}
    />
  );
};

export default VocabularyBookNote;
